import { apiClient, type ApiResponse } from '@/api';
import { AUTH_API_URLS } from '@/api/constants/api.url';
import type {
    DevLoginRequest,
    DevLoginResponse,
    ForgotPasswordRequest,
    LoginResult,
    ResendTwoFactorRequest,
    ResetPasswordRequest,
    VerifyTwoFactorRequest,
} from '@/modules/auth/types';

/**
 * Auth service — the only file in the module that talks to axios. Hooks +
 * pages stay declarative by going through here.
 *
 * Why a thin pass-through:
 *   - Axios already centralizes interceptors + error normalization.
 *   - The "service" name keeps room for future logic (e.g. trimming
 *     whitespace from credentials, mapping API enum casing).
 *   - One seam to mock in tests (`vi.mock('./auth.service')`).
 */
export const authService = {
    async devLogin(body: DevLoginRequest): Promise<LoginResult> {
        // Trim leading/trailing whitespace from credentials so a stray space
        // (common from copy-paste) never causes a false "invalid email or
        // password". Interior characters are left untouched.
        const credentials: DevLoginRequest = {
            ...body,
            email: body.email.trim(),
            password: body.password.trim(),
        };
        const { data } = await apiClient.post<ApiResponse<LoginResult>>(AUTH_API_URLS.devLogin, credentials);
        return data.data;
    },

    /** Second leg of a 2FA sign-in: exchange the challenge + code for a session. */
    async verifyTwoFactor(body: VerifyTwoFactorRequest): Promise<DevLoginResponse> {
        const { data } = await apiClient.post<ApiResponse<DevLoginResponse>>(
            AUTH_API_URLS.verifyTwoFactor, body,
        );
        return data.data;
    },

    /** Mail a fresh security code for an in-flight email-method challenge. */
    async resendTwoFactor(body: ResendTwoFactorRequest): Promise<{ email: string }> {
        const { data } = await apiClient.post<ApiResponse<{ email: string }>>(
            AUTH_API_URLS.resendTwoFactor, body,
        );
        return data.data;
    },

    async forgotPassword(body: ForgotPasswordRequest): Promise<void> {
        await apiClient.post<ApiResponse<Record<string, never>>>(AUTH_API_URLS.forgotPassword, body);
    },

    async resetPassword(body: ResetPasswordRequest): Promise<void> {
        await apiClient.post<ApiResponse<Record<string, never>>>(AUTH_API_URLS.resetPassword, body);
    },
};
